import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type RouterService from '@ember/routing/router-service';
import type SessionService from 'bcl/services/session';
import { LinkTo } from '@ember/routing';
import HeroIcon from 'bcl/components/hero-icon';
import { on } from '@ember/modifier';
import { next } from '@ember/runloop';

interface NavBarSignature {
  // The arguments accepted by the component
  Args: {};
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}

export default class NavBarComponent extends Component<NavBarSignature> {
  <template>
    <nav class="bg-gray-800 print:hidden">
      <div class="mx-auto max-w-7xl px-4 lg:px-8">
        <div class="flex h-16 items-center justify-between">
          <div class="flex items-center">
            <div class="shrink-0">
              <LinkTo @route="auth">
                <img
                  class="h-8 w-8"
                  src="/img/piceatech-logo.png"
                  alt="Your Company"
                />
              </LinkTo>
            </div>
            <div class="hidden lg:block">
              <div class="ml-10 flex items-baseline space-x-2">
                {{#if this.showNav}}
                  {{! Current: "bg-gray-900 text-white", Default: "text-gray-300 hover:bg-gray-700 hover:text-white" }}
                  {{#each this.navigation as |navItem|}}
                    <LinkTo
                      {{on "click" this.close}}
                      @route={{navItem.route}}
                      class="text-white rounded-md px-4 py-2 text-md font-medium hover:underline underline-offset-4 bg-gray-600"
                    >{{navItem.name}}</LinkTo>
                  {{/each}}
                {{/if}}
              </div>
            </div>
          </div>
          {{#if this.showNav}}
            <div class="hidden lg:block">
              <div class="ml-4 flex items-center md:ml-6">
                <button
                  type="button"
                  class="relative rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                >
                  <span class="absolute -inset-1.5"></span>
                  <span class="sr-only">View notifications</span>
                  <HeroIcon @icon="bell" class="size-6" />
                </button>

                {{! Profile dropdown }}
                <div class="relative ml-3">
                  <div>
                    <button
                      {{on "click" this.toggleOpen}}
                      type="button"
                      class="relative cursor-pointer flex max-w-xs items-center rounded-full bg-gray-800 text-sm text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                      id="user-menu-button"
                      aria-expanded="false"
                      aria-haspopup="true"
                    >
                      <span class="absolute -inset-1.5"></span>
                      <span class="sr-only">Open user menu</span>
                      <HeroIcon
                        @icon="bars-3"
                        class="size-6 {{if this.isOpen 'hidden' 'block'}}"
                      />
                      <HeroIcon
                        @icon="x-mark"
                        class="size-6 {{if this.isOpen 'block' 'hidden'}}"
                      />
                    </button>
                  </div>
                  <div
                    class="transition ease-out duration-100 cursor-pointer
                      {{if
                        this.isOpen
                        'transform opacity-100 scale-100'
                        'transform opacity-0 scale-95'
                      }}
                      absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-/5 focus:outline-none"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="user-menu-button"
                    tabindex="-1"
                  >
                    {{#if this.isOpen}}
                      {{! Active: "bg-gray-100", Not Active: "" }}
                      {{#each this.userNavigation as |navItem|}}
                        {{#if navItem.route}}
                          <LinkTo
                            {{on "click" this.close}}
                            @route={{navItem.route}}
                            class="block px-4 py-2 text-sm text-gray-700"
                            role="menuitem"
                            tabindex="-1"
                          >{{navItem.name}}</LinkTo>
                        {{else}}
                          <button
                            type="button"
                            {{on "click" navItem.action}}
                            class="block cursor-pointer w-full text-left px-4 py-2 text-sm text-gray-700"
                          >{{navItem.name}}</button>
                        {{/if}}
                      {{/each}}
                    {{/if}}
                  </div>
                </div>
              </div>
            </div>
          {{/if}}
          <div class="-mr-2 flex lg:hidden">
            {{! Mobile menu button }}
            <button
              {{on "click" this.toggleOpen}}
              type="button"
              class="relative inline-flex items-center justify-center rounded-md bg-gray-800 p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
              aria-controls="mobile-menu"
              aria-expanded="false"
            >
              <span class="absolute -inset-0.5"></span>
              <span class="sr-only">Open main menu</span>
              <HeroIcon
                @icon="bars-3"
                class="size-6 {{if this.isOpen 'hidden' 'block'}}"
              />
              <HeroIcon
                @icon="x-mark"
                class="size-6 {{if this.isOpen 'block' 'hidden'}}"
              />
            </button>
          </div>
        </div>
      </div>

      <div
        class="lg:hidden {{if this.isOpen 'block' 'hidden'}}"
        id="mobile-menu"
      >
        <div class="space-y-1 px-2 pb-3 pt-2 sm:px-3">
          {{#if this.showNav}}
            {{! Current: "bg-gray-900 text-white", Default: "text-gray-300 hover:bg-gray-700 hover:text-white" }}
            {{#each this.navigation as |navItem|}}
              <LinkTo
                {{on "click" this.toggleOpen}}
                @route={{navItem.route}}
                class="bg-gray-900 text-white block rounded-md px-3 py-2 text-base font-medium"
              >{{navItem.name}}</LinkTo>
            {{/each}}
          {{/if}}
        </div>
        {{#if this.showNav}}
          <div class="border-t border-gray-700 pb-3 pt-4">

            <div class="mt-3 space-y-1 px-2">
              {{#each this.userNavigation as |navItem|}}
                {{#if navItem.route}}
                  <LinkTo
                    @route={{navItem.route}}
                    class="block rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white"
                  >{{navItem.name}}</LinkTo>
                {{else}}
                  <button
                    type="button"
                    {{on "click" navItem.action}}
                    class="block rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white"
                  >{{navItem.name}}</button>
                {{/if}}
              {{/each}}
            </div>
          </div>
        {{/if}}
      </div>
    </nav>
  </template>
  @service declare router: RouterService;
  @service declare session: SessionService;
  @tracked isOpen = false;
  @action
  toggleOpen() {
    this.isOpen = !this.isOpen;
  }
  @action
  close(event) {
    next(this, () => {
      this.isOpen = false;
    });
  }
  get showNav() {
    return this.router.currentRoute?.name !== 'login';
  }
  @action async logout() {
    this.toggleOpen();
    await this.session.unauthenticate();
    this.router.transitionTo('login');
  }
  get navigation() {
    const nav = [
      { name: 'Übersicht', route: 'auth' },
      { name: 'Stammdaten', route: 'auth.basic-data' },
      { name: 'Lagerübersicht', route: 'auth.location' },
      { name: 'Bestellungen', route: 'auth.orders' },
    ];
    if (this.session.isAdmin) {
      nav.push({ name: 'Admin', route: 'auth.admin' });
    }
    return nav;
  }
  userNavigation = [
    { name: 'Ausloggen', action: this.logout },
    { name: 'Liste Vorschlagswerte', route: 'auth.views.proposed' },
    { name: 'Liste pro Lieferant', route: 'auth.views.suppliers' },
  ];
}
